import { useCallback, useEffect, useRef, useState } from 'react';
import { t } from '@/lib/i18n';

export const RAIL_MIN = 220;
export const RAIL_MAX = 720;
export const RAIL_DEFAULT = 288; // matches the old fixed w-72

const STORAGE_KEY = 'dam.rail.width';

function clamp(px: number) {
  return Math.min(RAIL_MAX, Math.max(RAIL_MIN, px));
}

/**
 * Remembered rail width. Deep folder paths blow past any fixed width, so the
 * user sets their own and it sticks across sessions.
 */
export function useRailWidth() {
  const [width, setWidth] = useState(() => {
    try {
      const saved = Number(window.localStorage.getItem(STORAGE_KEY));
      return saved ? clamp(saved) : RAIL_DEFAULT;
    } catch {
      return RAIL_DEFAULT;
    }
  });

  const persist = useCallback((px: number) => {
    const next = clamp(px);
    setWidth(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, String(next));
    } catch { /* non-fatal: the width still applies for this session */ }
  }, []);

  return { width, setWidth: persist };
}

/**
 * Drag handle between the rail and the content. Sits *on* the boundary rather
 * than taking layout space of its own, so the seam stays a hairline while the
 * grab target is comfortably wide.
 *
 * Pointer events (not mouse) so a trackpad, touch screen and stylus all work,
 * with capture so a fast drag that outruns the cursor doesn't drop the gesture.
 */
export function RailResizer({ width, onResize }: { width: number; onResize: (px: number) => void }) {
  const [dragging, setDragging] = useState(false);
  // Offset between the pointer and the actual edge, so the handle doesn't jump
  // to centre itself under the cursor on grab.
  const grabOffset = useRef(0);
  /** The pointer currently dragging, or null. See onPointerDown. */
  const activePointer = useRef<number | null>(null);

  // While dragging, suppress text selection and keep the resize cursor even as
  // the pointer strays over content that would otherwise set its own.
  useEffect(() => {
    if (!dragging) return;
    const prevCursor = document.body.style.cursor;
    const prevSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevSelect;
    };
  }, [dragging]);

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={t('railResizer.resizeNavigation')}
      aria-valuenow={width}
      aria-valuemin={RAIL_MIN}
      aria-valuemax={RAIL_MAX}
      tabIndex={0}
      onPointerDown={(e) => {
        e.preventDefault();
        grabOffset.current = e.clientX - width;
        // Track the gesture in a ref as well as state: `dragging` state drives
        // the visuals, but a ref is readable immediately, so the first
        // pointermove after pointerdown isn't dropped waiting for a re-render.
        activePointer.current = e.pointerId;
        e.currentTarget.setPointerCapture(e.pointerId);
        setDragging(true);
      }}
      onPointerMove={(e) => {
        if (activePointer.current !== e.pointerId) return;
        onResize(e.clientX - grabOffset.current);
      }}
      onPointerUp={(e) => {
        activePointer.current = null;
        if (e.currentTarget.hasPointerCapture(e.pointerId)) {
          e.currentTarget.releasePointerCapture(e.pointerId);
        }
        setDragging(false);
      }}
      onPointerCancel={() => {
        activePointer.current = null;
        setDragging(false);
      }}
      // Keyboard parity: the handle is focusable, so arrows must move it too.
      onKeyDown={(e) => {
        const step = e.shiftKey ? 48 : 16;
        if (e.key === 'ArrowLeft') { e.preventDefault(); onResize(width - step); }
        if (e.key === 'ArrowRight') { e.preventDefault(); onResize(width + step); }
        if (e.key === 'Home') { e.preventDefault(); onResize(RAIL_DEFAULT); }
      }}
      onDoubleClick={() => onResize(RAIL_DEFAULT)}
      title={t('railResizer.dragToResize')}
      className={cnResizer(dragging)}
    />
  );
}

/**
 * -translate-x-1/2 centres the 9px grab strip on the seam; the visible line is
 * the 1px child, so the hit area is wide without a wide-looking border.
 */
function cnResizer(dragging: boolean) {
  return [
    // `hidden md:block` lives here rather than on a wrapper: these sizing
    // classes must apply to the flex child itself (see AppShell).
    'hidden md:block',
    // 15px grab strip, centred on the seam by -ml-[7px]. The original 9px was
    // an easy target to miss: the handle is invisible until hovered, so anyone
    // who doesn't know it exists has to find it by accident.
    'group relative z-20 -ml-[7px] w-[15px] shrink-0 cursor-col-resize touch-none',
    // The visible seam: a hairline normally, thickening to 2px on hover/drag so
    // the boundary announces itself as draggable when the pointer nears it.
    'after:absolute after:inset-y-0 after:left-1/2 after:-translate-x-1/2',
    'after:transition-all after:content-[""]',
    dragging
      ? 'after:w-[2px] after:bg-brand-500'
      : 'after:w-px after:bg-white/10 hover:after:w-[2px] hover:after:bg-brand-500/70',
    'focus-visible:outline-none focus-visible:after:w-[2px] focus-visible:after:bg-brand-500',
  ].join(' ');
}
